import React, { useMemo, useEffect } from 'react';
import { Box, VStack, HStack, Text, NumberInput, InputGroup, Accordion, Icon } from '@chakra-ui/react';
import { FiBriefcase, FiMapPin, FiInfo } from 'react-icons/fi';
import { useTranslation } from '../../hooks/useTranslation';
import { useGetCustomerQuery } from '../../api';
import PageHeader from '../../components/base/PageHeader';
import LoadingState from '../../components/base/LoadingState';
import BaseSelect from '../../components/base/BaseSelect';
import Button from '../../components/base/Button';
import { IoLogoEuro } from 'react-icons/io5';
import { alertManager } from '../../utils/alertManager';
import { useTariff } from '../../hooks/useTariff';
import { usePaymentAmount } from '../../hooks/usePaymentAmount';
import { usePaymentPeriods, PaymentPeriod } from '../../hooks/usePaymentPeriods';
import RequisiteItem from '../../components/payment/RequisiteItem';
import { Tooltip } from '../../components/base/Tooltip';
import { isStandalone } from '../../utils/deviceUtils';
import { render } from '../../utils/templateUtils';
import { getPaymentLinkUrl } from '../../api/constants';

const PaymentPayPage: React.FC = () => {
    const { t } = useTranslation();
    const { data: customer, isLoading } = useGetCustomerQuery();
    const { tariff } = useTariff();

    // Вычисляем долг из баланса
    const debt = useMemo(() => {
        if (customer && customer.balance < 0) {
            return Math.abs(customer.balance);
        }
        return 0;
    }, [customer]);

    // Используем хук для работы с суммой оплаты
    const {
        amount: amountData,
        setAmount,
        setAmountByMonths,
        getAmountByMonths,
        minAmount,
        getDiscountInfoByMonths,
    } = usePaymentAmount({
        monthlyPrice: tariff.price_with_vat,
        debt,
        discountsForPeriods: customer?.discounts_for_periods,
        minPeriodForPayment: customer?.status === 'new' ? 3 : undefined,
    });

    // Долг добавляется отдельно и не ограничивает доступные периоды оплаты.
    const minMonths = customer?.status === 'new' ? 3 : 1;

    // Используем хук для работы с периодами
    const { selectedPeriod, periodCollection, handlePeriodChange, setCustomPeriod } = usePaymentPeriods({
        onPeriodChange: (period) => {
            if (period.value === 'custom') {
                setAmount(minAmount);
            } else {
                setAmountByMonths(period.months);
            }
        },
        minMonths,
    });

    const setCustomAmount = (amount: number) => {
        setAmount(amount);
        setCustomPeriod();
    };

    // Обработчик изменения суммы в инпуте
    const handleAmountChange = (details: NumberInput.ValueChangeDetails) => {
        setCustomAmount(details.valueAsNumber || 0);
    };

    // Обновляем сумму выбранного периода при изменении тарифа или задолженности.
    useEffect(() => {
        if (tariff.price_with_vat > 0 && selectedPeriod.value !== 'custom') {
            // Считаем той же формулой, что и при смене периода в селекте, — со скидкой за период
            const calculatedAmount = getAmountByMonths(selectedPeriod.months);
            if (calculatedAmount < minAmount) {
                setAmount(minAmount);
                setCustomPeriod();
            } else {
                setAmount(calculatedAmount);
            }
        }
    }, [
        tariff.price_with_vat,
        debt,
        minAmount,
        customer?.discounts_for_periods,
        selectedPeriod.value,
        selectedPeriod.months,
        getAmountByMonths,
        setAmount,
        setCustomPeriod,
    ]);

    const handlePay = async () => {
        if (amountData.isValid) {
            try {
                const url = getPaymentLinkUrl(Number(amountData.amount.toFixed(2)));
                // В PWA (standalone) window.open с _blank часто блокируется — переходим в текущем окне
                if (isStandalone()) {
                    window.location.href = url;
                } else {
                    window.open(url, '_blank', 'noopener,noreferrer');
                }
            } catch (error) {
                alertManager.error(t.pages.payment.serverError);
            }
        }
    };

    // Функция для рендеринга опций с подсветкой скидок
    const renderOptionLabel = (option: PaymentPeriod) => {
        const discountInfo = getDiscountInfoByMonths(option.months);
        return (
            <HStack justify="space-between" w="full">
                <Text>{option.label}</Text>
                {discountInfo.hasDiscount && (
                    <Text fontSize="sm" color="black" fontWeight="medium">
                        {t.pages.payment.discount} {discountInfo.discount}%
                    </Text>
                )}
            </HStack>
        );
    };

    if (isLoading || !customer) {
        return <LoadingState message={t.pages.support.loading} />;
    }

    return (
        <Box
            h="calc(100vh - 20px)"
            bg="gray.100"
            px={4}
            pt={2}
            maxW="4xl"
            pb={{ base: 20, lg: 6 }}
            overflow="auto"
        >
            <PageHeader title={t.pages.payment.paymentScreenTitle} backPath="/payment" />

            <VStack gap={6} align="stretch" w={{ base: 'full', md: '600px' }} mx="auto">
                <Box p={6} bg="white">
                    <VStack gap={4} align="stretch" w="full">
                        {/* Заголовок */}
                        <Text fontSize="lg" fontWeight="bold" color="black">
                            {t.pages.payment.payOnline}
                        </Text>

                        {/* Селект периода */}
                        <VStack align="stretch" gap={2}>
                            <BaseSelect
                                collection={periodCollection}
                                value={selectedPeriod.value}
                                onValueChange={handlePeriodChange}
                                renderOptionLabel={renderOptionLabel}
                            />
                        </VStack>

                        <VStack align="stretch" gap={3}>
                            {/* Инпут суммы — только при выборе "Своя сумма" */}
                            {selectedPeriod.value === 'custom' && (
                                <VStack align="stretch" gap={2}>
                                    <Text fontSize="sm" color="gray.600">
                                        {t.pages.payment.replenishBalanceOn}
                                    </Text>
                                    <NumberInput.Root
                                        value={amountData.amountDisplay}
                                        onValueChange={handleAmountChange}
                                        min={minAmount}
                                        step={1}
                                        formatOptions={{ minimumFractionDigits: 2, maximumFractionDigits: 2 }}
                                        size="lg"
                                    >
                                        <InputGroup startElement={<IoLogoEuro />}>
                                            <NumberInput.Input
                                                placeholder="0.00"
                                                h="48px"
                                                fontSize="16px"
                                                borderRadius="0"
                                            />
                                        </InputGroup>
                                        <NumberInput.Control>
                                            <NumberInput.IncrementTrigger />
                                            <NumberInput.DecrementTrigger />
                                        </NumberInput.Control>
                                    </NumberInput.Root>
                                    {/* Показываем ошибку валидации */}
                                    {!amountData.isValid && (
                                        <Text fontSize="sm" color="red.500">
                                            {render(t.pages.payment.minAmountError, {
                                                amount: minAmount.toFixed(2),
                                            })}
                                        </Text>
                                    )}
                                </VStack>
                            )}
                            {/* Показываем информацию о скидке */}
                            {amountData.hasDiscount && (
                                <HStack gap={2} align="center">
                                    <Box
                                        pl={3}
                                        py={1}
                                        borderLeftWidth="4px"
                                        borderLeftStyle="solid"
                                        borderLeftColor="yellow.500"
                                        display="inline-flex"
                                        alignItems="center"
                                    >
                                        <Text fontSize="lg" color="black" fontWeight="medium">
                                            {render(t.pages.payment.discountOnTariff, {
                                                discount: amountData.discount ?? 0,
                                                months: amountData.discountDuration ?? 0,
                                            })}
                                        </Text>
                                    </Box>
                                    <Tooltip
                                        text={render(t.pages.payment.discountOnTariffTooltip, {
                                            amount: amountData.amount.toFixed(2),
                                            discount: amountData.discount ?? 0,
                                            months: amountData.discountDuration ?? 0,
                                        })}
                                    >
                                        <Box display="inline-flex" alignItems="center" alignSelf="center">
                                            <Box
                                                as="span"
                                                cursor="pointer"
                                                color="yellow.600"
                                                _hover={{ opacity: 0.6 }}
                                                display="inline-flex"
                                                alignItems="center"
                                            >
                                                <Icon as={FiInfo} boxSize={5} />
                                            </Box>
                                        </Box>
                                    </Tooltip>
                                </HStack>
                            )}
                            <HStack gap={2} align="center">
                                <Text fontSize="lg" color="black" fontWeight="700">
                                    {t.pages.payment.totalToPay}
                                </Text>
                                <Text fontSize="lg" color="black" fontWeight="700">
                                    {amountData.amount.toFixed(2)}€
                                </Text>
                            </HStack>
                        </VStack>

                        {/* Кнопка оплаты */}
                        <Button
                            buttonType="success"
                            onClick={handlePay}
                            w="full"
                            h="48px"
                            disabled={!amountData.isValid}
                        >
                            {t.pages.payment.pay}
                        </Button>
                    </VStack>
                </Box>
                {/* Блок оплаты по реквизитам */}
                <Box p={6} bg="white">
                    <Accordion.Root collapsible>
                        <Accordion.Item value={'requisites'}>
                            <Accordion.ItemTrigger>
                                <Text fontSize="lg" fontWeight="medium" color="black" w="full">
                                    {t.pages.payment.payByRequisites}
                                </Text>
                                <Accordion.ItemIndicator />
                            </Accordion.ItemTrigger>
                            <Accordion.ItemContent>
                                <Accordion.ItemBody>
                                    <VStack align="flex-start" gap={1} fontSize="sm" color="black" pt={2}>
                                        <RequisiteItem icon={FiBriefcase} value="Batexpress SRL" />
                                        <RequisiteItem label="VAT:" value="BE0846.402.796" />
                                        <RequisiteItem
                                            icon={FiMapPin}
                                            value="14 Rue de Grand-Bigard, 1082 Bruxelles, Belgique"
                                        />
                                        <RequisiteItem label="IBAN:" value="BE13 0020 1899 1039" />
                                        <RequisiteItem label="BIC:" value="GEBABEBB" />
                                    </VStack>
                                </Accordion.ItemBody>
                            </Accordion.ItemContent>
                        </Accordion.Item>
                    </Accordion.Root>
                </Box>
            </VStack>
        </Box>
    );
};

export default PaymentPayPage;
